import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Profile {
  id: string;
  organization_id: string | null;
  full_name: string | null;
  avatar_url: string | null;
  language_preference: string | null;
  email_notifications_enabled: boolean;
  tracking_opt_out: boolean | null;
  dfy_roi_hourly_rate: number | null;
  is_super_admin?: boolean | null;
}

interface Organization {
  id: string;
  name: string;
  industry: string | null;
  team_size: string | null;
  website: string | null;
  timezone: string | null;
}

interface UserRole {
  id: string;
  user_id: string;
  organization_id: string;
  role: "owner" | "manager" | "staff";
  role_name: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface UserProfileData {
  profile: Profile | null;
  organization: Organization | null;
  userRole: UserRole | null;
}

async function fetchUserProfile(userId: string): Promise<UserProfileData> {
  // Fetch profile
  const { data: profileData, error: profileError } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (profileError) throw profileError;

  if (!profileData?.organization_id) {
    return {
      profile: profileData,
      organization: null,
      userRole: null,
    };
  }

  // Fetch organization and user role in parallel
  const [orgResult, roleResult] = await Promise.all([
    supabase
      .from("organizations")
      .select("*")
      .eq("id", profileData.organization_id)
      .maybeSingle(),
    supabase
      .from("user_roles")
      .select("*")
      .eq("user_id", userId)
      .eq("organization_id", profileData.organization_id)
      .maybeSingle(),
  ]);

  if (orgResult.error) throw orgResult.error;
  if (roleResult.error) throw roleResult.error;

  return {
    profile: profileData,
    organization: orgResult.data,
    userRole: roleResult.data,
  };
}

/**
 * Hook for fetching user profile, organization, and role with TanStack Query caching.
 * Data is cached for 10 minutes and shared across all components.
 *
 * To update the profile cache after changes, use:
 * - notifyProfileUpdate() - invalidates the cache (backward compatible)
 * - useInvalidateUserProfile() - React hook for invalidation
 */
export function useUserProfile() {
  const { user, loading: authLoading } = useAuth();

  const { data, isPending, isFetching, error } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: () => fetchUserProfile(user!.id),
    enabled: !authLoading && !!user,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  // Keep loading while auth is unresolved, or while org status is still being verified.
  // This prevents route guards from briefly treating users as "not onboarded" during
  // transient refetches that still have null organization data.
  const organizationKnown = data?.organization ?? null;
  const waitingForOrganizationResolution =
    isFetching && !error && organizationKnown === null;
  const loading =
    authLoading || (!!user && (isPending || waitingForOrganizationResolution));

  return {
    profile: data?.profile ?? null,
    organization: data?.organization ?? null,
    userRole: data?.userRole ?? null,
    loading,
    error,
  };
}

/**
 * Hook to get the invalidation function for user profile cache.
 * Call this after updating profile, organization, or role.
 */
export function useInvalidateUserProfile() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ["user-profile"] });
  };
}

// Store queryClient reference for notifyProfileUpdate
let globalQueryClient: ReturnType<typeof useQueryClient> | null = null;

/**
 * Internal hook to capture queryClient reference.
 * Called once from a component that's always mounted.
 */
export function useSetupProfileInvalidation() {
  const queryClient = useQueryClient();
  globalQueryClient = queryClient;
}

/**
 * Backward-compatible function to notify profile updates.
 * This invalidates the TanStack Query cache.
 *
 * @deprecated Prefer using useInvalidateUserProfile() hook instead
 */
export function notifyProfileUpdate() {
  if (globalQueryClient) {
    globalQueryClient.invalidateQueries({ queryKey: ["user-profile"] });
  } else {
    console.warn("notifyProfileUpdate called before queryClient was set up");
  }
}
